import axios from 'axios';

const NUMISHUB_API = process.env.REACT_APP_NUMISHUB_API_URL ?? 'http://localhost:3006';
const ADMIN_KEY   = process.env.REACT_APP_NUMISHUB_ADMIN_KEY ?? 'numishub-admin-dev-key';

const nhApi = axios.create({
  baseURL: `${NUMISHUB_API}/admin`,
  headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
  timeout: 15000,
});

export interface NumisHubTenant {
  id: string;
  slug: string;
  name: string;
  country: string;
  country_name: string;
  domain: string | null;
  active: number;
  plan: 'starter' | 'pro' | 'enterprise';
  plan_expires_at: string | null;
  created_at: string;
  updated_at: string;
  db_exists?: number;
}

export interface NumisHubStats {
  total: number;
  active: number;
  starter: number;
  pro: number;
  enterprise: number;
}

export interface NumisHubTenantStats {
  coins:  { total: number; stock_total: number };
  users:  { total: number; active: number };
  orders: { total: number; paid: number; pending: number; revenue: number };
}

export interface CreateTenantInput {
  slug: string;
  name: string;
  country: string;
  country_name: string;
  plan?: string;
  domain?: string;
}

export interface NumisHubCoin {
  id: string;
  slug: string;
  title: string;
  series: string | null;
  year: number;
  metal: string;
  price: number;
  currency: string;
  stock: number;
  mintage: number | null;
  status: 'available' | 'sold_out' | 'upcoming' | 'discontinued';
  created_at: string;
  image: string | null;
}

export interface NumisHubOrder {
  id: string;
  reference: string;
  status: string;
  total_amount: number;
  currency: string;
  payment_ref: string | null;
  created_at: string;
  user_name: string;
  user_email: string;
  item_count: number;
}

export interface NumisHubUser {
  id: string;
  name: string;
  email: string;
  role: 'bank_admin' | 'bank_staff';
  active: number;
  created_at: string;
}

export interface CreateTenantUserInput {
  name: string;
  email: string;
  password: string;
  role: 'bank_admin' | 'bank_staff';
}

class NumisHubService {
  async listTenants(): Promise<{ tenants: NumisHubTenant[]; stats: NumisHubStats }> {
    const { data } = await nhApi.get('/tenants');
    return data;
  }

  async getTenant(slug: string): Promise<{ tenant: NumisHubTenant; stats: NumisHubTenantStats | null }> {
    const { data } = await nhApi.get(`/tenants/${slug}`);
    return data;
  }

  async createTenant(input: CreateTenantInput): Promise<NumisHubTenant> {
    const { data } = await nhApi.post('/tenants', input);
    return data.tenant;
  }

  async updateTenant(slug: string, updates: Partial<Pick<NumisHubTenant, 'plan' | 'active' | 'domain' | 'name'>>): Promise<NumisHubTenant> {
    const { data } = await nhApi.patch(`/tenants/${slug}`, updates);
    return data.tenant;
  }

  async deactivateTenant(slug: string): Promise<void> {
    await nhApi.delete(`/tenants/${slug}`);
  }

  async listTenantCoins(slug: string): Promise<NumisHubCoin[]> {
    const { data } = await nhApi.get(`/tenants/${slug}/coins`);
    return data.coins;
  }

  async listTenantOrders(slug: string): Promise<NumisHubOrder[]> {
    const { data } = await nhApi.get(`/tenants/${slug}/orders`);
    return data.orders;
  }

  async listTenantUsers(slug: string): Promise<NumisHubUser[]> {
    const { data } = await nhApi.get(`/tenants/${slug}/users`);
    return data.users;
  }

  async createTenantUser(slug: string, input: CreateTenantUserInput): Promise<NumisHubUser> {
    const { data } = await nhApi.post(`/tenants/${slug}/users`, input);
    return data.user;
  }
}

export default new NumisHubService();
